import React, { FC, ReactElement, useRef } from 'react';
import {
  Popover as ChakraPopover,
  PopoverTrigger,
  PopoverContent,
  PopoverBody,
  PopoverFooter,
  PopoverArrow,
  PopoverCloseButton,
  Button,
  Flex,
  useDisclosure,
  ButtonProps,
  PopoverProps,
  PopoverContentProps,
  useOutsideClick,
  PopoverBodyProps,
  PopoverFooterProps,
  Portal,
} from '@chakra-ui/react';

type ChildProps = {
  onClose?: () => void;
};

type Props = {
  triggerElement: React.ReactNode;
  children: ReactElement<ChildProps>; // Ensure children receive the correct prop
  ctaLabel?: string;
  ctaOnClick?: (args) => void;
  secondaryCtaLabel?: string;
  secondaryCtaProps?: ButtonProps;
  ctaProps?: ButtonProps;
  secondaryCtaOnClick?: (args) => void;
  showArrow?: boolean;
  showCloseButton?: boolean;
  popoverProps?: PopoverProps;
  popoverBodyProps?: PopoverBodyProps;
  popoverContentProps?: PopoverContentProps;
  popoverActions?: any;
  popoverFooterProps?: PopoverFooterProps;
  footer?: React.ReactNode | React.ComponentType<any>;
  /**
   * Render the content in a portal on <body>, above the page's own stacking
   * and clipping. Off by default; inline content can be covered by the
   * borders of the card it opens from.
   */
  usePortal?: boolean;
};

export const KPopover: FC<Props> = ({
  triggerElement,
  children,
  ctaLabel,
  ctaOnClick,
  secondaryCtaLabel,
  ctaProps,
  secondaryCtaProps,
  secondaryCtaOnClick,
  showArrow = true,
  showCloseButton = true,
  footer = null,
  popoverProps = {},
  popoverContentProps = {},
  popoverBodyProps = {},
  popoverActions = {},
  popoverFooterProps = {},
  usePortal = false,
}) => {
  const popoverDisclosure = useDisclosure();
  const { isOpen, onOpen, onClose } = popoverActions;
  const popoverRef = useRef(null);
  const childWithProps = React.isValidElement(children)
    ? React.cloneElement(children, { onClose })
    : children;

  useOutsideClick({
    ref: popoverRef,
    handler: onClose,
  });

  const content = (
    <PopoverContent ref={popoverRef} {...popoverContentProps}>
      {showArrow && <PopoverArrow />}
      {showCloseButton && <PopoverCloseButton />}

      <PopoverBody {...popoverBodyProps}>{childWithProps}</PopoverBody>

      {React.isValidElement(footer) ? (
        footer
      ) : (
        <>
          {(ctaLabel || secondaryCtaLabel) && (
            <PopoverFooter {...popoverFooterProps}>
              <Flex gap="10px" w="100%" justifyContent="end">
                {secondaryCtaLabel && (
                  <Button
                    variant="secondary"
                    onClick={(props) => {
                      if (secondaryCtaOnClick) {
                        secondaryCtaOnClick({ popoverDisclosure, ...props });
                      } else onClose();
                    }}
                    {...secondaryCtaProps}
                  >
                    {secondaryCtaLabel}
                  </Button>
                )}
                {ctaLabel && (
                  <Button
                    variant="primary"
                    onClick={(props) =>
                      ctaOnClick({ popoverDisclosure, ...props })
                    }
                    {...ctaProps}
                  >
                    {ctaLabel}
                  </Button>
                )}
              </Flex>
            </PopoverFooter>
          )}
        </>
      )}
    </PopoverContent>
  );

  return (
    <ChakraPopover
      initialFocusRef={null}
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      autoFocus={false}
      closeOnBlur={true}
      {...popoverProps}
    >
      {/* @ts-expect-error -- Chakra PopoverTrigger types its child as a single element */}
      <PopoverTrigger>{triggerElement}</PopoverTrigger>
      {usePortal ? <Portal>{content}</Portal> : content}
    </ChakraPopover>
  );
};
